<template>
  <div class="release-page py-8">
    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-24">
      <div class="w-8 h-8 border-4 border-white/10 rounded-full border-t-primary animate-spin"></div>
    </div>

    <div v-else-if="release">
      <!-- Back -->
      <NuxtLink to="/" class="rd-back">
        <NuxtIcon name="mdi:arrow-left" /> {{ $t('shop.back_to_shop', 'Back to shop') }}
      </NuxtLink>

      <div class="flex flex-col md:flex-row gap-6 md:gap-8">
        <!-- Artwork — HUD module frame -->
        <div class="w-full md:w-80 md:shrink-0 mx-auto md:mx-0 max-w-sm">
          <CyberPanel notch="md" :brackets="true" glow="idle">
            <div class="aspect-square overflow-hidden">
              <img :src="artworkUrl" :alt="release.name" class="w-full h-full object-cover" />
            </div>
          </CyberPanel>
        </div>

        <!-- Info -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center flex-wrap gap-2">
            <CyberChip tone="signal" size="md">{{ release.releaseFormatType }}</CyberChip>
            <NuxtLink
              v-if="release.genre?.name"
              :to="`/genre/${slugifyGenre(release.genre.name)}`"
              class="rd-genre-link"
            >
              <CyberChip tone="neutral" size="md">{{ release.genre.name }}</CyberChip>
            </NuxtLink>
            <CyberChip
              v-if="isPeered"
              tone="signal"
              size="md"
              :title="`Sold by ${sellerName}`"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3"><path d="M12 2 4 6v6c0 5 3.5 9.7 8 11 4.5-1.3 8-6 8-11V6l-8-4z"/></svg>
              {{ $t('shop.sold_by', { seller: sellerName }) }}
            </CyberChip>
          </div>

          <h1 class="rd-title">{{ release.name }}</h1>
          <div class="rd-artists">
            <template v-for="(a, i) in (release.artists || [])" :key="a.artist?._id || i">
              <span v-if="i > 0" class="rd-artist-sep">,</span>
              <NuxtLink
                v-if="a.artist?.slug"
                :to="`/artists/${a.artist.slug}`"
                class="rd-artist-link"
              >{{ a.artist.name }}</NuxtLink>
            </template>
            <span v-if="!release.artists?.length" class="rd-artist-name">{{ release.displayArtist }}</span>
          </div>

          <!-- Mono data strip: label · genre · date -->
          <CyberDataStrip :items="metaItems" class="mt-3" />

          <ShopSaleBanner v-if="release.sale" :sale="release.sale" class="mt-5" />

          <!-- Pricing / Add to Cart — MP3 is THE CTA, WAV the quiet frame -->
          <div class="mt-6 flex flex-wrap items-center gap-3">
            <CyberButton variant="primary" @click="addMp3ToCart">
              <NuxtIcon name="mdi:cart-plus" />
              MP3 ·
              <ShopSalePrice
                :price="release.shopPrice"
                :list-price="release.shopPriceList"
                :percent="release.salePercentMp3"
              />
            </CyberButton>
            <CyberButton v-if="release.shopPriceWav" variant="ghost" @click="addWavToCart">
              <NuxtIcon name="mdi:cart-plus" />
              WAV ·
              <ShopSalePrice
                :price="release.shopPriceWav"
                :list-price="release.shopPriceWavList"
                :percent="release.salePercentWav"
              />
            </CyberButton>
          </div>
          <ShopVatNote variant="detail" pickable class="mt-2 block" />

          <!-- Track list — console rows -->
          <div class="mt-8">
            <h3 class="rd-section">{{ $t('shop.tracks_heading', 'Tracks') }}</h3>
            <ol class="rd-tracks">
              <li
                v-for="(track, index) in release.tracks"
                :key="track._id"
                class="rd-track group"
                :class="{ playing: isTrackPlaying(track._id) }"
              >
                <span class="rd-track-index">{{ String(index + 1).padStart(2, '0') }}</span>
                <button
                  v-if="track.audio?.mp3?.url"
                  class="rd-track-play"
                  :class="{ on: isTrackPlaying(track._id) }"
                  :aria-label="isTrackPlaying(track._id) ? 'Pause' : 'Play'"
                  @click="togglePlay(track)"
                >
                  <svg v-if="isTrackPlaying(track._id)" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
                </button>
                <span v-else class="rd-track-play rd-track-play--none" aria-hidden="true"></span>
                <p class="rd-track-name">{{ trackDisplayTitle(track) }}</p>
                <span class="rd-track-time">{{ formatDuration(track.duration) }}</span>
                <!-- Per-track buy keys. Always visible on touch devices —
                     a :hover reveal leaves them unreachable on phones. -->
                <div class="track-buy-actions rd-track-buy">
                  <button
                    class="rd-buy-key"
                    @click="addTrackToCart(track, 'mp3')"
                    :title="`Buy MP3 - ${formatPrice(trackPrice(track, 'mp3'))}`"
                  >
                    <span class="rd-buy-fmt">MP3</span>
                    <ShopSalePrice
                      size="sm"
                      :price="trackPrice(track, 'mp3')"
                      :list-price="trackListPrice(track, 'mp3')"
                      :percent="trackSalePercent(track, 'mp3')"
                    />
                  </button>
                  <button
                    class="rd-buy-key"
                    @click="addTrackToCart(track, 'wav')"
                    :title="`Buy WAV - ${formatPrice(trackPrice(track, 'wav'))}`"
                  >
                    <span class="rd-buy-fmt">WAV</span>
                    <ShopSalePrice
                      size="sm"
                      :price="trackPrice(track, 'wav')"
                      :list-price="trackListPrice(track, 'wav')"
                      :percent="trackSalePercent(track, 'wav')"
                    />
                  </button>
                </div>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <ShopBundleStrip :release="String(release._id)" />
    </div>

    <!-- Fetch failed -->
    <div v-else-if="fetchError" class="text-center py-24 text-text-muted">
      <p class="font-bold mb-2">{{ $t('shop.release_load_failed', 'Could not load this release') }}</p>
      <button class="cy-pgbtn" @click="fetchRelease()">{{ $t('shop.retry') }}</button>
    </div>

    <!-- Not found (real 404) -->
    <div v-else class="text-center py-24 text-text-muted">
      <p class="font-bold">{{ $t('shop.release_not_found', 'Release not found') }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue'
// Explicit imports — Nuxt auto-import is unreliable for nested
// component dirs (Shop/Cyber/*).
import CyberPanel from '~/components/Shop/Cyber/CyberPanel.vue'
import CyberChip from '~/components/Shop/Cyber/CyberChip.vue'
import CyberButton from '~/components/Shop/Cyber/CyberButton.vue'
import CyberDataStrip from '~/components/Shop/Cyber/CyberDataStrip.vue'
import ShopSalePrice from '~/components/Shop/SalePrice.vue'
import ShopSaleBanner from '~/components/Shop/SaleBanner.vue'
import { slugifyGenre } from '~/utils/genreSlug'
import { useShareMeta, useShareFormat } from '~/composables/useShareMeta'
import ShopBundleStrip from '~/components/Shop/BundleStrip.vue'

definePageMeta({
  layout: 'shop'
})

const route = useRoute()
const { addToCart } = useCart()
const player = useAudioPlayer()
const { currentTrack, isPlaying } = player
const { tenant } = useTenantTheme()
const { t } = useI18n()

// Loaded during SSR, not in onMounted: link previews (WhatsApp, Discord,
// Facebook, X) only ever see the server-rendered head, and a client-side
// fetch left every shared release previewing as just the shop name.
const { data: release, pending: loading, error: loadError, refresh: fetchRelease } = await useFetch(
  () => `/api/shop/releases/${route.params.slug}`,
  { default: () => null }
)
// A 404 is "release not found" (the v-else branch); any other status is a
// real network/server problem and gets the retry UI.
const fetchError = computed(() => !!loadError.value && loadError.value.statusCode !== 404)
if (import.meta.server && loadError.value?.statusCode === 404) {
  setResponseStatus(useRequestEvent(), 404)
}
if (loadError.value && loadError.value.statusCode !== 404) {
  console.error('[shop/releases/:slug] fetch failed', loadError.value)
}

// Cross-tenant attribution. Direct visitors to a peered release link must see
// who's selling — same shield pill the storefront card already shows.
const sellerTenantId = computed(() => {
  const t = release.value?.tenant
  if (!t) return null
  return typeof t === 'object' ? (t._id || t.id) : t
})
const isPeered = computed(() =>
  sellerTenantId.value && tenant.value?._id && String(sellerTenantId.value) !== String(tenant.value._id)
)
const sellerName = computed(() => {
  const t = release.value?.tenant
  if (t && typeof t === 'object') return t.name || 'Partner label'
  return 'Partner label'
})

const isTrackPlaying = (trackId) => {
  return currentTrack.value?.id === trackId && isPlaying.value
}

const togglePlay = (track) => {
  const audioUrl = track.audio?.mp3?.url
  if (!audioUrl) return
  player.toggle({
    id: track._id,
    url: audioUrl,
    title: `${trackDisplayTitle(track)} — ${release.value?.displayArtist || ''}`,
    imageUrl: release.value?.artwork?.low?.url || release.value?.artwork?.thumbnail?.url || release.value?.artwork?.original?.url,
    audio: { waveform: track.audio?.waveform || [] },
    previewStart: track.previewStart || 0,
    previewLength: track.previewLength || 30,
    previewOffset: track.previewOffset || 0,
    // The FULL track length. The waveform is drawn from the whole song, so
    // without this the player scales it by the <audio> element's duration —
    // which holds only the preview slice. See MasterPlayer `fullDuration`.
    duration: track.duration || 0,
  })
}

const artworkUrl = computed(() => {
  const art = release.value?.artwork
  const url = art?.original?.url || art?.thumbnail?.url || art?.url
  if (url) return `/api/assets/serve?url=${encodeURIComponent(url)}`
  return 'https://placehold.co/400x400/1a1a2e/666?text=No+Art'
})

// The mono data strip under the artists: label / genre / release date.
const metaItems = computed(() => {
  const items = []
  if (release.value?.label?.name) items.push({ value: release.value.label.name })
  if (release.value?.genre?.name) items.push({ value: release.value.genre.name })
  if (release.value?.consumerReleaseDate) items.push({ value: formatDate(release.value.consumerReleaseDate) })
  return items
})

// Funnel: release-page view to both ad pixels (consent-gated in the plugins).
// Client-only; re-fires when a retry or a slug change brings another release.
const trackView = () => {
  const r = release.value
  if (!r?._id) return
  useShopTracking().viewContent({
    id: String(r._id),
    name: r.name,
    priceEur: Number(r.pricing?.mp3 ?? r.price) || undefined,
  })
}
onMounted(trackView)
watch(() => release.value?._id, (id, prev) => { if (id && id !== prev) trackView() })

// ── Share / search meta ───────────────────────────────────────────────
// "LEGEND – Cirillo JR" with "EP by Cirillo JR · Bassfeature · 2 tracks ·
// Released Sep 11, 2026. Download: MP3 €2.83 · WAV €3.56." and the cover.
const shareFormat = useShareFormat()
const FORMAT_KEY = { SINGLE: 'format_single', EP: 'format_ep', ALBUM: 'format_album' }
const RELEASE_TYPE = {
  SINGLE: 'https://schema.org/SingleRelease',
  EP: 'https://schema.org/EPRelease',
  ALBUM: 'https://schema.org/AlbumRelease',
}
// artwork.low is a plain string on some releases, { url } on others.
const artUrl = (v) => (typeof v === 'string' ? v : v?.url) || ''
const isoDuration = (sec) => {
  const n = Math.round(Number(sec) || 0)
  return n > 0 ? `PT${Math.floor(n / 60)}M${n % 60}S` : undefined
}

useShareMeta(() => {
  const r = release.value
  if (!r) return null
  const artist = r.displayArtist || ''
  const format = t(`shop.seo.${FORMAT_KEY[r.releaseFormatType] || 'format_release'}`)
  const tracks = (r.tracks || []).filter(Boolean)
  const count = r.trackCount || r.totalTracks || tracks.length
  const date = r.consumerReleaseDate || r.originalReleaseDate
  const upcoming = date && new Date(date).getTime() > Date.now()
  const facts = [
    artist ? t('shop.seo.format_by', { format, artist }) : format,
    r.label?.name,
    count ? t('shop.seo.tracks', count) : '',
    date ? t(upcoming ? 'shop.seo.upcoming' : 'shop.seo.released', { date: shareFormat.date(date) }) : '',
  ].filter(Boolean)
  const prices = [
    r.shopPrice ? `MP3 ${shareFormat.price(r.shopPrice)}` : '',
    r.shopPriceWav ? `WAV ${shareFormat.price(r.shopPriceWav)}` : '',
  ].filter(Boolean).join(' · ')
  const description = `${facts.join(' · ')}.${prices ? ` ${t('shop.seo.download', { prices })}` : ''}`
  const art = r.artwork || {}
  const seller = (r.tenant && typeof r.tenant === 'object' && r.tenant.name) || tenant.value?.name
  // Product only with a price: Google rejects a Product that has no offer.
  const offer = r.shopPrice
    ? {
        '@type': 'Offer',
        price: Number(r.shopPrice).toFixed(2),
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        ...(r.sale?.endsAt ? { priceValidUntil: String(r.sale.endsAt).slice(0, 10) } : {}),
        ...(seller ? { seller: { '@type': 'Organization', name: seller } } : {}),
      }
    : null
  return {
    title: artist ? `${r.name} – ${artist}` : r.name,
    description,
    // The master first: the proxy scales it to 1200 px. `low` is 250 px.
    image: artUrl(art.original) || artUrl(art.low) || artUrl(art) || artUrl(art.thumbnail),
    imageAlt: artist ? `${r.name} – ${artist}` : r.name,
    type: 'music.album',
    meta: date ? [{ property: 'music:release_date', content: String(date).slice(0, 10) }] : [],
    jsonLd: {
      '@type': offer ? ['MusicAlbum', 'Product'] : 'MusicAlbum',
      name: r.name,
      description,
      ...(artist ? { byArtist: { '@type': 'MusicGroup', name: artist } } : {}),
      ...(RELEASE_TYPE[r.releaseFormatType] ? { albumReleaseType: RELEASE_TYPE[r.releaseFormatType] } : {}),
      ...(count ? { numTracks: count } : {}),
      ...(r.genre?.name ? { genre: r.genre.name } : {}),
      ...(date ? { datePublished: String(date).slice(0, 10) } : {}),
      ...(r.label?.name ? { recordLabel: { '@type': 'Organization', name: r.label.name } } : {}),
      ...(offer ? { sku: String(r._id), ...(r.label?.name ? { brand: { '@type': 'Brand', name: r.label.name } } : {}), offers: offer } : {}),
      ...(tracks.length
        ? {
            track: tracks.map(tr => ({
              '@type': 'MusicRecording',
              name: trackDisplayTitle(tr),
              ...(artist ? { byArtist: { '@type': 'MusicGroup', name: artist } } : {}),
              ...(isoDuration(tr.duration) ? { duration: isoDuration(tr.duration) } : {}),
            })),
          }
        : {}),
    },
  }
}, { notFoundTitle: 'Release not found' })

// Pull seller off the populated release.tenant once so all three cart-add
// flows (album MP3, album WAV, single track) include it. Cart groups items
// by seller and warns multi-seller carts before checkout.
const sellerInfo = () => {
  const t = release.value?.tenant
  return t && typeof t === 'object'
    ? { _id: String(t._id || ''), name: t.name || '' }
    : null
}

const addMp3ToCart = () => {
  addToCart({
    _id: `release_${release.value._id}_mp3`,
    releaseId: release.value._id,
    type: 'release',
    title: release.value.name,
    artist: release.value.displayArtist,
    price: release.value.shopPrice,
    listPrice: release.value.shopPriceList,
    salePercent: release.value.salePercentMp3,
    format: 'mp3',
    slug: release.value.slug,
    artworkUrl: artworkUrl.value,
    seller: sellerInfo()
  })
}

const addWavToCart = () => {
  addToCart({
    _id: `release_${release.value._id}_wav`,
    releaseId: release.value._id,
    type: 'release',
    title: release.value.name,
    artist: release.value.displayArtist,
    price: release.value.shopPriceWav,
    listPrice: release.value.shopPriceWavList,
    salePercent: release.value.salePercentWav,
    format: 'wav',
    slug: release.value.slug,
    artworkUrl: artworkUrl.value,
    seller: sellerInfo()
  })
}

// Prefer the track's own price — it carries a hand-set override and the sale.
// The release-level value is the tenant default and only a fallback.
const trackPrice = (track, format) => (format === 'wav'
  ? (track.priceWav ?? release.value.trackPriceWav)
  : (track.priceMp3 ?? release.value.trackPriceMp3))

// Null unless the sale actually cuts this track's price — SalePrice renders no
// strikethrough for null, which is exactly what a non-discounted track needs.
const trackListPrice = (track, format) => (format === 'wav'
  ? (track.priceWavList ?? release.value.trackPriceWavList)
  : (track.priceMp3List ?? release.value.trackPriceMp3List))

// The track's OWN effective discount, never the release's. They diverge: the
// bundle price is capped at what its tracks cost individually, and a single
// track's discount is eaten by the 50-cent floor long before a bundle's is.
// At €0.99 with a 60% sale a track is really 49% off while the 8-track bundle
// is the full 60% — reading the release value here advertised a cut the track
// does not get.
const trackSalePercent = (track, format) => (format === 'wav'
  ? (track.salePercentWav ?? release.value.salePercentWav)
  : (track.salePercentMp3 ?? release.value.salePercentMp3))

const addTrackToCart = (track, format) => {
  addToCart({
    _id: `track_${track._id}_${format}`,
    trackId: track._id,
    releaseId: release.value._id,
    type: 'track',
    title: trackDisplayTitle(track),
    artist: release.value.displayArtist,
    price: trackPrice(track, format),
    listPrice: trackListPrice(track, format),
    salePercent: trackSalePercent(track, format),
    format,
    slug: release.value.slug,
    artworkUrl: artworkUrl.value,
    seller: sellerInfo()
  })
}

const formatPrice = (price) => {
  if (!price) return 'Free'
  return '\u20AC' + Number(price).toFixed(2)
}

const formatDuration = (seconds) => {
  if (!seconds) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

const formatDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}
</script>

<style scoped>
/* Console-row tracklist + detail grammar on the --cy-* tokens. */
.rd-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 24px;
  font-family: var(--cy-mono);
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-muted);
  text-decoration: none;
  transition: color 0.15s;
}
.rd-back:hover { color: var(--primary); }

.rd-genre-link { text-decoration: none; }

.rd-title {
  font-family: var(--cy-display);
  font-size: var(--cy-size-title);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.04;
  color: var(--text-main);
  margin: 10px 0 0;
  overflow-wrap: break-word;
}

.rd-artists {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-family: var(--cy-mono);
  font-size: 0.85rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.rd-artist-link {
  color: var(--text-muted);
  text-decoration: none;
  transition: color 0.15s;
}
.rd-artist-link:hover { color: var(--primary); }
.rd-artist-name { color: var(--text-muted); }
.rd-artist-sep { color: var(--text-dim); }

.rd-section {
  font-family: var(--cy-display);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-main);
  margin: 0 0 10px;
  padding-bottom: 8px;
  position: relative;
}
.rd-section::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background: linear-gradient(to right, var(--cy-rim-hot), var(--cy-rim) 30%, transparent 92%);
}

/* Tracklist — console rows on a single module frame. */
.rd-tracks {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--cy-rim);
  border-radius: 3px;
  background: var(--cy-module);
  overflow: hidden;
}
.rd-track {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 14px;
  border-top: 1px solid var(--cy-line);
  transition: background 0.15s ease;
  min-width: 0;
}
.rd-track:first-child { border-top: none; }
.rd-track:hover { background: rgb(var(--primary-channels) / 0.04); }
.rd-track.playing {
  background: rgb(var(--primary-channels) / 0.07);
  box-shadow: inset 2px 0 0 var(--primary);
}

.rd-track-index {
  font-family: var(--cy-mono);
  font-variant-numeric: tabular-nums;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-dim);
  min-width: 20px;
  text-align: right;
  flex-shrink: 0;
}
.rd-track.playing .rd-track-index { color: var(--primary); }

.rd-track-play {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  border: 1px solid var(--cy-rim);
  background: rgb(var(--primary-channels) / 0.05);
  color: var(--text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
}
.rd-track-play:hover {
  border-color: var(--cy-rim-hot);
  color: var(--primary);
}
.rd-track-play.on {
  background: var(--primary);
  border-color: var(--primary);
  color: var(--cy-ground);
  box-shadow: var(--cy-glow);
}
.rd-track-play--none {
  border: none;
  background: transparent;
  cursor: default;
}

.rd-track-name {
  flex: 1;
  min-width: 0;
  font-size: 0.85rem;
  color: var(--text-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
}

.rd-track-time {
  font-family: var(--cy-mono);
  font-variant-numeric: tabular-nums;
  font-size: 0.7rem;
  color: var(--text-dim);
  flex-shrink: 0;
}

.rd-track-buy {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.rd-buy-key {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 9px;
  border: 1px solid var(--cy-rim);
  border-radius: 3px;
  background: rgb(var(--primary-channels) / 0.05);
  cursor: pointer;
  transition: all 0.15s;
}
.rd-buy-key:hover {
  border-color: var(--cy-rim-hot);
  background: rgb(var(--primary-channels) / 0.12);
  box-shadow: var(--cy-glow);
}
.rd-buy-fmt {
  font-family: var(--cy-mono);
  font-size: 0.58rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-dim);
}
.rd-buy-price {
  font-family: var(--cy-mono);
  font-variant-numeric: tabular-nums;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--text-main);
}
.rd-buy-key:hover .rd-buy-fmt,
.rd-buy-key:hover .rd-buy-price { color: var(--primary); }
.rd-buy-key:hover :deep(.sprice:not(.is-sale) .sprice__now) { color: var(--primary); }

/* Pointer-capable inputs: keep the hover-reveal so the track list reads
   calm at rest. Touch / coarse pointers see the buy actions immediately. */
@media (hover: hover) and (pointer: fine) {
  .track-buy-actions {
    opacity: 0;
    transition: opacity 0.15s ease;
  }
  .rd-track:hover .track-buy-actions,
  .rd-track:focus-within .track-buy-actions { opacity: 1; }
}

/* Touch-target floor for the row keys on coarse pointers. */
@media (hover: none), (pointer: coarse) {
  .rd-buy-key { height: 38px; }
  .rd-track-play { width: 38px; height: 38px; }
}

/* Phone: stack the per-track buy buttons under the title so each key
   gets enough touch-target width on narrow screens. */
@media (max-width: 480px) {
  .rd-track { flex-wrap: wrap; }
  .rd-track-buy {
    flex-basis: 100%;
    justify-content: flex-end;
    padding-left: 62px;
  }
  .rd-buy-key { flex: 1; justify-content: center; }
}
</style>
